import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { DisplayProps, SpaceProps } from 'styled-system';
import { display } from 'styled-system';

import ContainerWithBackground from 'common/components/ContainerWithBackground';
import { Flex } from 'common/components/Flex';
import ButtonGroup from 'common/components/Reclamation/ButtonGroup';
import OrderOverview from 'common/components/Reclamation/OrderOverview';
import { TrackingEvent, TrackingEventElement } from 'common/enums/Reclamation';
import { useLocalization } from 'common/providers/LocaleProvider';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import type { ReclamationOrder } from 'common/types/reclamation';
import { useRequestSubmit } from './hooks';

export interface ReclamationRecapOrderOverviewProps {
  order: ReclamationOrder;
  isMarketplace: boolean;
  backLinkUrl: Nullable<string>;
  reclamationId?: string;
  successRedirectUrl?: string;
  isExternalMarketplace?: boolean;
}

const Container = styled(ContainerWithBackground)<DisplayProps & SpaceProps>`
  ${display};
`;
const containerStyles: ComponentProps<typeof Container> = {
  display: ['block', 'flex', 'block'],
  px: ['sp_12', 'sp_16', null, 'sp_24'],
  py: ['sp_16', null, null, 'sp_20'],
};

const OrderOverviewContainer = styled(Flex)``;

const ReclamationRecapOrderOverview = ({
  order,
  isMarketplace,
  reclamationId,
  successRedirectUrl,
  backLinkUrl,
  isExternalMarketplace,
}: ReclamationRecapOrderOverviewProps) => {
  const { t } = useLocalization();
  const [isLoading, handleRequestSubmit] = useRequestSubmit(reclamationId, successRedirectUrl);
  const proceedButtonText = t('h24_confirm_request');
  const backLinkText = t(
    successRedirectUrl
      ? 'h24_back_to_selection'
      : isExternalMarketplace
        ? 'h24_back_to_article_selection'
        : 'h24_back_to_order_details'
  );

  const handleBackLinkClick = () => {
    dispatchTrackingEventGA4({
      event: TrackingEvent.ReclamationRecapOrderOverviewClick,
      element: TrackingEventElement.Link,
      link_text: backLinkText,
    });
  };

  const handleProceedButtonClick = () => {
    dispatchTrackingEventGA4({
      event: TrackingEvent.ReclamationRecapOrderOverviewClick,
      element: TrackingEventElement.Button,
      link_text: proceedButtonText,
    });

    handleRequestSubmit();
  };

  return (
    <div data-corgi-id="reclamation-recap-order-overview">
      <Container {...containerStyles}>
        <OrderOverviewContainer flex={1}>
          <OrderOverview
            {...order}
            isMarketplace={isMarketplace}
            isExternalMarketplace={isExternalMarketplace}
          />
        </OrderOverviewContainer>
        <ButtonGroup
          backLinkUrl={backLinkUrl}
          backLinkText={backLinkText}
          proceedButtonText={proceedButtonText}
          isLoading={isLoading}
          isDisabled={isLoading}
          isProceedVisible={!!successRedirectUrl}
          onProceedClick={handleProceedButtonClick}
          onBackLinkClick={handleBackLinkClick}
        />
      </Container>
    </div>
  );
};

export default ReclamationRecapOrderOverview;
